// Phase 129 Plan 04 Task 1 — Demo short-code card.
//
// Composition order matches the Phase 127 quick-start Step 1 card so the
// marketing demo and the in-app sandbox are visually identical:
//   1. CardHeader  — "Step 1" eyebrow
//   2. CardContent — heading + subheading
//                    DL row: Our WhatsApp Number + copy
//                    DL row: Code + copy
//                    Right column: QR (above) + "Send In WhatsApp" (below)
//                    Status row: spinner + "Waiting For Your Message…"
// DESIGN.md tokens applied: dark immersive surface (#181A1B), 24px card radius,
// dual-shadow elevation, pill (100px) CTA in Meta Blue #0064E0, Optimistic VF
// stylistic sets on the code, generous gallery padding, no em dashes.
import { useState, useRef } from 'react';

import { Card, CardContent } from '../ui/card';
import { ClientOnlyQrCode } from './client-only-qr-code';
import { formatSandboxPhone } from '../../lib/format-phone';
import { emit } from '../../lib/analytics';
import { getOsCopyShortcut } from '../../lib/platform';

interface DemoCodeCardProps {
  code: string;
  sandboxPhone: string;
  demoSessionId?: string | null;
  channel: 'whatsapp' | 'instagram';
}

// Build-time Vite env var — the Instagram sandbox handle. Staging defaults to
// `hookmyappsandboxstaging` (no underscore, per operator 2026-05-24).
// Production sets its own value via VITE_SANDBOX_IG_USERNAME in .env.production.
const SANDBOX_IG_USERNAME =
  (import.meta.env.VITE_SANDBOX_IG_USERNAME as string | undefined) ??
  'hookmyappsandboxstaging';

function CopyIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function CopyButton({
  value,
  label,
  surface,
  demoSessionId,
  targetRef,
  onFailure,
}: {
  value: string;
  label: string;
  surface: 'demo_code' | 'demo_phone';
  demoSessionId?: string;
  targetRef: React.RefObject<HTMLElement | null>;
  onFailure: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);

  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setFailed(false);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      setFailed(true);
      setCopied(false);
      // Auto-select the visible value so the user only has to press the OS
      // copy shortcut to capture it. selectAllChildren works on element nodes;
      // the parent <span> wraps the rendered text directly.
      const node = targetRef.current;
      if (node) {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.selectAllChildren(node);
        }
      }
      const name = (err as { name?: string })?.name ?? 'Error';
      const message = (err as { message?: string })?.message;
      emit('demo_code_copy_failed', {
        demo_session_id: demoSessionId,
        surface,
        reason: name,
        error_message: message ? message.slice(0, 200) : undefined,
      });
      onFailure();
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex size-7 items-center justify-center rounded-md text-white/50 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0064E0]"
      aria-label={label}
      title={label}
    >
      {failed ? <span aria-hidden="true" className="text-xs">!</span> : copied ? <CheckIcon /> : <CopyIcon />}
    </button>
  );
}

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin text-[#0064E0]"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

export function DemoCodeCard({
  code,
  sandboxPhone,
  demoSessionId,
  channel,
}: DemoCodeCardProps) {
  // Normalize null → undefined so CopyButton and emit() receive string | undefined.
  const sessionId = demoSessionId ?? undefined;
  // wa.me prefills the code; ig.me does NOT support a reliable prefill across
  // IG mobile clients (see spec Open Questions), so the deep link is plain
  // and the user pastes / types the code in the IG DM thread.
  const waLink = `https://wa.me/${sandboxPhone}?text=${encodeURIComponent(code)}`;
  const igLink = `https://ig.me/m/${SANDBOX_IG_USERNAME}`;
  const deepLink = channel === 'whatsapp' ? waLink : igLink;
  const formatted = formatSandboxPhone(sandboxPhone);
  const phoneRef = useRef<HTMLSpanElement | null>(null);
  const codeRef = useRef<HTMLSpanElement | null>(null);
  const recipientRef = useRef<HTMLSpanElement | null>(null);
  const [showFailureHint, setShowFailureHint] = useState(false);
  const shortcut = getOsCopyShortcut();
  const isWhatsapp = channel === 'whatsapp';

  return (
    <Card>
      <CardContent className="px-8 py-8 md:px-10 md:py-10">
        <div className="space-y-8">
          <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-start md:gap-12">
            {/* Left column — heading, subheading, DL */}
            <div className="space-y-6">
              <div className="space-y-2">
                <h2 className="text-2xl md:text-3xl font-medium leading-[1.15] tracking-[-0.02em] text-white">
                  {isWhatsapp
                    ? 'Send Us This Code Via WhatsApp'
                    : 'Send Us This Code Via Instagram'}
                </h2>
                <p className="text-base leading-relaxed text-white/60">
                  {isWhatsapp
                    ? 'Tap the button or scan the QR code'
                    : (
                      <>
                        DM <strong>@{SANDBOX_IG_USERNAME}</strong> on Instagram with the code below.
                      </>
                    )}
                </p>
              </div>
              <dl className="space-y-4">
                <div>
                  <dt className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/45">
                    {isWhatsapp ? 'Our WhatsApp Number' : 'Our Instagram'}
                  </dt>
                  <dd className="mt-1.5 flex items-center gap-2">
                    {isWhatsapp ? (
                      <span
                        ref={phoneRef}
                        className="text-lg font-medium tabular-nums tracking-tight text-white"
                      >
                        {formatted}
                      </span>
                    ) : (
                      <span
                        ref={recipientRef}
                        className="text-lg font-medium tracking-tight text-white"
                      >
                        @{SANDBOX_IG_USERNAME}
                      </span>
                    )}
                    <CopyButton
                      value={isWhatsapp ? `+${sandboxPhone}` : `@${SANDBOX_IG_USERNAME}`}
                      label={isWhatsapp ? 'Copy number' : 'Copy handle'}
                      surface="demo_phone"
                      demoSessionId={sessionId}
                      targetRef={isWhatsapp ? phoneRef : recipientRef}
                      onFailure={() => setShowFailureHint(true)}
                    />
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/45">
                    Code
                  </dt>
                  <dd className="mt-1.5 flex items-center gap-2">
                    <span
                      ref={codeRef}
                      data-testid="demo-short-code"
                      className="font-mono text-lg font-medium tracking-[0.06em] text-white"
                    >
                      {code}
                    </span>
                    <CopyButton
                      value={code}
                      label="Copy code"
                      surface="demo_code"
                      demoSessionId={sessionId}
                      targetRef={codeRef}
                      onFailure={() => setShowFailureHint(true)}
                    />
                  </dd>
                </div>
              </dl>
              {showFailureHint && (
                <p
                  role="status"
                  className="text-xs text-amber-200/90"
                >
                  Copy failed. Select the code and press {shortcut}.
                </p>
              )}
            </div>

            {/* Right column — QR (above) + Send In WhatsApp (below) */}
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                {/* Tight Meta-Blue ambient halo — sized to the tile so it
                    doesn't bleed onto the CTA below. */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 -m-3 rounded-[28px] bg-[#0064E0]/15 blur-2xl"
                />
                {/* Conic Meta-Blue rim — subtle gradient stroke catching
                    light from one side. Lower-opacity than before so it
                    reads as edge-light, not a glow. */}
                <div
                  aria-hidden="true"
                  className="absolute -inset-px rounded-[22px] bg-gradient-to-br from-white/30 via-[#0064E0]/20 to-transparent"
                />
                {/* Frosted-glass tile — translucent white with backdrop blur,
                    inner highlight stroke, dual-shadow elevation. QR module
                    is rendered with a transparent background so the glass
                    reads through; Meta-Blue dots on the frosted surface keep
                    scanner contrast. */}
                <div
                  data-testid="demo-qr-code"
                  className="relative rounded-[20px] border border-white/30 bg-white/65 p-3 backdrop-blur-xl shadow-[0_12px_28px_rgba(0,0,0,0.35),0_2px_4px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.55)]"
                >
                  <ClientOnlyQrCode
                    value={deepLink}
                    size={148}
                    backgroundColor="transparent"
                  />
                </div>
              </div>
              <a
                href={deepLink}
                target="_blank"
                rel="noreferrer noopener"
                onClick={(e) => {
                  // Touch devices: same-tab navigation. Android Chrome's
                  // "wants to access other apps and services on this device"
                  // prompt is triggered by the `target="_blank"` + custom-
                  // scheme-redirect combo (wa.me → whatsapp://, ig.me →
                  // instagram://). A top-level navigation hands off via
                  // Android App Links / iOS Universal Links silently — and
                  // on touch devices the OS pushes the target app to the
                  // foreground anyway, so the marketing tab is never
                  // visually replaced.
                  if (
                    typeof window !== 'undefined' &&
                    window.matchMedia('(pointer: coarse)').matches
                  ) {
                    e.preventDefault();
                    window.location.href = deepLink;
                  }
                }}
                className="inline-flex w-full items-center justify-center rounded-[100px] bg-[#0064E0] px-8 py-4 text-base font-medium text-white transition hover:bg-[#0143B5] active:bg-[#004BB9] md:w-auto"
              >
                {isWhatsapp ? 'Send In WhatsApp' : 'Open Instagram'}
              </a>
            </div>
          </div>

          <div
            aria-hidden="true"
            className="h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"
          />
          <div
            role="status"
            aria-live="polite"
            className="flex items-center justify-center gap-2 text-sm text-white/60"
          >
            <Spinner />
            Waiting For Your Message…
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
